import { useState } from "react";
import { useMutation } from "@tanstack/react-query";
import { api, type ActionResult, type EditableStoryboard, type KeywordDetail } from "../../lib/api.js";
import { buildMaestroBrief, copyToClipboard, downloadText } from "../../lib/maestro-brief.js";
import { ImportMaestroVideo } from "./ImportMaestroVideo.js";

/**
 * The article → Maestro → import loop, in one card, reachable straight from the
 * Article tab:
 *   1. Create the storyboard (תסריט) from the article.
 *   2. Copy/Download it as a brief to build the video by hand in Maestro.
 *   3. Import the finished Maestro clip back onto the keyword.
 * (The automatic path — Studio tab → Viral video — stays as-is; this is the
 * hand-crafted-in-Maestro alternative.)
 */
export function MaestroHandoff({
  keywordId,
  detail,
  onChanged,
}: {
  keywordId: string;
  detail: KeywordDetail;
  onChanged: () => void;
}) {
  const sb = (detail.keyword.storyboard as EditableStoryboard | null) ?? null;
  const hasArticle = !!detail.keyword.articleMarkdown;
  const [note, setNote] = useState<string | null>(null);

  const gen = useMutation({
    // Keyed so the panel's action bar shows it as "in progress" too.
    mutationKey: ["gen", keywordId, "storyboard"],
    mutationFn: () => api.generateStoryboard(keywordId),
    onSuccess: (r: ActionResult) => { setNote(r.summary ?? "Storyboard created."); onChanged(); },
    onError: (e: Error) => setNote(e.message),
  });

  const exportBrief = async (mode: "copy" | "download") => {
    if (!sb) return;
    const text = buildMaestroBrief(sb);
    if (mode === "download") {
      downloadText(`maestro-brief-${keywordId}.txt`, text);
      setNote("Brief downloaded.");
      return;
    }
    setNote((await copyToClipboard(text)) ? "Brief copied — paste it into Maestro." : "Copy blocked — use Download instead.");
  };

  return (
    <section className="space-y-3 rounded-md border border-line bg-white p-3">
      <div className="flex items-center justify-between gap-2">
        <h3 className="text-sm font-semibold">Make a video with Maestro</h3>
        <span className="text-2xs text-muted">storyboard → build in Maestro → import</span>
      </div>

      {/* 1 — storyboard from the article */}
      <div className="flex flex-wrap items-center gap-2">
        <button
          className="btn-primary"
          disabled={gen.isPending || !hasArticle}
          onClick={() => gen.mutate()}
          title={!hasArticle ? "Produce the article first" : sb ? "Regenerate the storyboard from the article" : "Create the storyboard from the article"}
        >
          {gen.isPending ? "Generating…" : sb ? "Regenerate storyboard" : "Create storyboard"}
        </button>
        {sb ? (
          <span className="text-2xs text-emerald-700">Storyboard ✓ · {sb.scenes.length} scenes · {sb.totalSec}s</span>
        ) : (
          <span className="text-2xs text-muted">no storyboard yet — create it from the article</span>
        )}
      </div>

      {/* 2 — hand the brief to Maestro */}
      {sb && (
        <div className="flex flex-wrap items-center gap-2">
          <button className="btn-secondary" onClick={() => void exportBrief("copy")}>Copy for Maestro</button>
          <button className="btn-ghost" onClick={() => exportBrief("download")}>Download brief</button>
          <span className="text-2xs text-muted">scene-by-scene: narration, visual, caption, timing</span>
        </div>
      )}

      {/* 3 — import the finished Maestro clip */}
      <div className="border-t border-line pt-3">
        <div className="mb-1.5 text-2xs text-muted">When your Maestro clip is ready, upload it here:</div>
        <ImportMaestroVideo keywordId={keywordId} onImported={onChanged} />
      </div>

      {note && <p className="text-2xs text-muted">{note}</p>}
    </section>
  );
}
